import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Edit3, Trash2, X, Check, ArrowLeft } from "lucide-react";
import PartnerAgreementText from "@/components/PartnerAgreementText";
import { ATTESTATION_ITEMS, AGREEMENT_VERSION } from "@/lib/partnerAgreement";

const EMOJI_PICKS = ["🧒","🦄","🦖","🐱","🐶","🦊","🐼","🐧","🐯","🦁","🐸","🐵","🦋","🌟","⚽","🎨"];

export default function KidsManage() {
  const { user } = useAuth();
  const [kids, setKids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // kid object
  const [form, setForm] = useState({ name: "", avatar_emoji: "🧒", age: "", account_type: "child", charity_number: "", signatory_role: "", attest: [false, false, false, false] });
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/kids"); setKids(data || []); }
    catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (user === false) return <div className="min-h-screen bg-[#FFFDF7]"><Navbar /><div className="p-20 text-center text-slate-500">Please <Link to="/auth?mode=login" className="text-blue-600 font-semibold">sign in</Link></div></div>;

  const openCreate = () => { setEditing(null); setForm({ name: "", avatar_emoji: "🧒", age: "", account_type: "child", charity_number: "", signatory_role: "", attest: [false, false, false, false] }); setCreating(true); setErr(""); };
  const openEdit = (k) => { setEditing(k); setForm({ name: k.name, avatar_emoji: k.avatar_emoji || "🧒", age: k.age ?? "", account_type: k.account_type || "child", charity_number: k.charity_number || "", signatory_role: "", attest: [false, false, false, false] }); setCreating(true); setErr(""); };

  const save = async (e) => {
    e.preventDefault();
    setErr("");
    const isOrg = form.account_type === "organization";
    // Iteration 294 — Commercial Participation Agreement attestation is
    // mandatory when onboarding (or converting to) an organization account.
    const needsSignature = isOrg && (!editing || editing.account_type !== "organization");
    if (needsSignature) {
      if (!(form.attest || []).every(Boolean)) {
        setErr("Please confirm all four partnership declarations to continue.");
        return;
      }
      if (!(form.signatory_role || "").trim()) {
        setErr("Please enter your role in the organization (e.g. PTA Treasurer).");
        return;
      }
    }
    const payload = { name: form.name, avatar_emoji: form.avatar_emoji, age: isOrg || form.age === "" ? null : parseInt(form.age, 10), account_type: form.account_type, charity_number: isOrg ? (form.charity_number || null) : null };
    if (needsSignature) {
      payload.partner_agreement_accepted = true;
      payload.signatory_role = form.signatory_role.trim();
    }
    try {
      if (editing) await api.patch(`/kids/${editing.id}`, payload);
      else await api.post("/kids", payload);
      setCreating(false); load();
    } catch (ex) { setErr(formatApiError(ex)); }
  };

  const remove = async (k) => {
    if (!window.confirm(`Remove ${k.name}'s profile? Any listings under their name will be archived.`)) return;
    try { await api.delete(`/kids/${k.id}`); load(); }
    catch (ex) { setErr(formatApiError(ex)); }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Navbar />
      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-10">
        <Link to="/dashboard/parent" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-blue-600" data-testid="kids-back"><ArrowLeft className="w-4 h-4" /> Back to dashboard</Link>
        <div className="flex flex-wrap items-end justify-between gap-4 mt-4">
          <div>
            <div className="pill bg-blue-100 text-blue-800">Family</div>
            <h1 className="font-display text-4xl md:text-5xl font-semibold text-slate-900 mt-3" data-testid="kids-heading">Manage children</h1>
            <p className="text-slate-600 mt-2">Add a profile for each child who'll be selling on MyTableTop.</p>
          </div>
          <button onClick={openCreate} className="btn-primary" data-testid="kids-add-btn"><Plus className="w-4 h-4" /> Add child</button>
        </div>

        {err && <div className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="kids-error">{err}</div>}

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="kids-grid">
          {loading ? Array.from({ length: 3 }).map((_, i) => <div key={i} className="card-base p-6 h-32 animate-pulse" />)
            : kids.length === 0 ? (
            <div className="col-span-full card-base p-10 text-center text-slate-500" data-testid="kids-empty">
              No children added yet. <button onClick={openCreate} className="text-blue-600 font-semibold hover:underline">Add your first one →</button>
            </div>
          ) : kids.map((k) => (
            <div key={k.id} className="card-base p-6 flex items-center gap-4" data-testid={`kids-card-${k.id}`}>
              <Link to={`/kids/${k.id}`} className="w-14 h-14 rounded-2xl bg-blue-100 text-blue-700 text-3xl flex items-center justify-center">{k.avatar_emoji}</Link>
              <div className="flex-1 min-w-0">
                <Link to={`/kids/${k.id}`} className="font-display text-xl font-medium text-slate-900 hover:text-blue-600 transition truncate block">{k.name}</Link>
                {k.account_type === "organization" ? (
                  k.org_status === "verified" ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-teal-100 text-teal-800 border border-teal-200 mt-1" data-testid={`kids-card-org-${k.id}`}>
                      Verified Cause
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-900 border border-amber-200 mt-1" data-testid={`kids-card-org-pending-${k.id}`}>
                      Organization · Pending Verification
                    </span>
                  )
                ) : (
                  <div className="text-sm text-slate-500">Age {k.age ?? "—"}</div>
                )}
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(k)} className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid={`kids-edit-${k.id}`}><Edit3 className="w-4 h-4 text-slate-600" /></button>
                <button onClick={() => remove(k)} className="w-9 h-9 rounded-full hover:bg-rose-100 flex items-center justify-center" data-testid={`kids-delete-${k.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {creating && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="card-base p-8 max-w-md w-full" data-testid="kids-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl font-medium">{editing ? "Edit child" : "Add a child"}</h2>
              <button type="button" onClick={() => setCreating(false)} className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center" data-testid="kids-modal-close"><X className="w-4 h-4" /></button>
            </div>

            {/* Iteration 284 — sub-account profile type selector. */}
            <label className="block text-sm font-bold text-slate-700 mb-1">Profile type</label>
            <div className="grid grid-cols-2 gap-2 mb-4" data-testid="kids-modal-type-picker">
              {[
                { key: "child", label: "Family Member", sub: "Child", emoji: "🧒" },
                { key: "organization", label: "Organization / Cause", sub: "PTA, School, Charity", emoji: "🏫" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setForm({ ...form, account_type: opt.key })}
                  aria-pressed={form.account_type === opt.key}
                  className={`rounded-2xl border-2 p-3 text-left transition ${form.account_type === opt.key ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"}`}
                  data-testid={`kids-modal-type-${opt.key}`}
                >
                  <div className="text-xl">{opt.emoji}</div>
                  <div className="text-sm font-bold text-slate-900 mt-1">{opt.label}</div>
                  <div className="text-[11px] text-slate-500">{opt.sub}</div>
                </button>
              ))}
            </div>

            <label className="block text-sm font-bold text-slate-700 mb-1">
              {form.account_type === "organization" ? "Organization Name" : "Name"}
            </label>
            <input
              required
              className="input-base mb-4"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={form.account_type === "organization" ? "e.g. St. Mary's PTA" : "First name"}
              data-testid="kids-modal-name"
            />

            <label className="block text-sm font-bold text-slate-700 mb-1">Avatar</label>
            <div className="grid grid-cols-8 gap-2 mb-4" data-testid="kids-modal-emoji-picker">
              {EMOJI_PICKS.map((e) => (
                <button key={e} type="button" onClick={() => setForm({ ...form, avatar_emoji: e })}
                  className={`text-2xl w-10 h-10 rounded-xl flex items-center justify-center ${form.avatar_emoji === e ? "bg-blue-500 ring-2 ring-blue-500" : "bg-slate-100 hover:bg-slate-200"}`}>{e}</button>
              ))}
            </div>

            {form.account_type === "organization" && (
              <>
                <label className="block text-sm font-bold text-slate-700 mb-1">Registered Charity Number / DfE URN (optional)</label>
                <input
                  className="input-base mb-1"
                  value={form.charity_number}
                  onChange={(e) => setForm({ ...form, charity_number: e.target.value })}
                  placeholder="e.g. 1154570 or SC012345"
                  data-testid="kids-modal-charity-number"
                />
                <p className="text-[11px] text-slate-500 mb-4">
                  We check this against the Charity Register. Organization accounts stay <strong>pending verification</strong> until the review completes — payouts are bank-transfer only.
                </p>
              </>
            )}

            {/* Iteration 294 — mandatory Commercial Participation Agreement
                attestation when onboarding (or converting to) an org account. */}
            {form.account_type === "organization" && (!editing || editing.account_type !== "organization") && (
              <div className="mb-4 rounded-2xl border border-teal-200 bg-teal-50/60 p-4" data-testid="partner-agreement-block">
                <div className="text-sm font-bold text-slate-800 mb-1">Commercial Participation Agreement</div>
                <p className="text-[11px] text-slate-600 mb-2">
                  Review the full{" "}
                  <Link to="/legal/partner-agreement" target="_blank" className="text-brandTeal font-semibold underline" data-testid="partner-agreement-view-link">PTA &amp; Charity Partner Terms</Link>{" "}
                  (6 sections) — opens in a new tab.
                </p>
                <div className="max-h-36 overflow-y-auto rounded-xl border border-slate-200 bg-white p-3 mb-3 text-[11px] text-slate-600" data-testid="partner-agreement-excerpt">
                  <PartnerAgreementText compact />
                </div>
                {ATTESTATION_ITEMS.map((label, i) => (
                  <label key={i} className="flex items-start gap-2 mb-2 cursor-pointer" data-testid={`partner-attest-${i}`}>
                    <input
                      type="checkbox"
                      className="mt-0.5 w-4 h-4 accent-teal-600"
                      checked={form.attest[i]}
                      onChange={(e) => setForm({ ...form, attest: form.attest.map((v, j) => (j === i ? e.target.checked : v)) })}
                      data-testid={`partner-attest-checkbox-${i}`}
                    />
                    <span className="text-[11px] text-slate-700 leading-snug">{label}</span>
                  </label>
                ))}
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 mt-3 mb-1">Your role in the organization</label>
                <input
                  className="input-base !py-2"
                  value={form.signatory_role}
                  onChange={(e) => setForm({ ...form, signatory_role: e.target.value })}
                  placeholder="e.g. PTA Treasurer, Headteacher, Club Secretary"
                  data-testid="partner-signatory-role"
                />
                <p className="text-[10px] text-slate-500 mt-2">
                  Digitally signed on submission — timestamp, signatory role and network address recorded (agreement v{AGREEMENT_VERSION}).
                </p>
              </div>
            )}

            {form.account_type !== "organization" && (
              <>
                <label className="block text-sm font-bold text-slate-700 mb-1">Age (optional)</label>
                <input type="number" min="2" max="17" className="input-base mb-4" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} placeholder="e.g. 9" data-testid="kids-modal-age" />
              </>
            )}

            {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3 mb-3" data-testid="kids-modal-error">{err}</div>}

            <button type="submit" className="btn-primary w-full" data-testid="kids-modal-save">
              <Check className="w-4 h-4" /> {editing ? "Save changes" : "Add child"}
            </button>
          </form>
        </div>
      )}

      <Footer />
    </div>
  );
}
